import type React from "react"
import { useTranslation } from "react-i18next"
import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { Box, Typography, AppBar, Toolbar, IconButton, Snackbar, Alert, Button, Tooltip } from "@mui/material"
import { Fullscreen as FullscreenIcon, Close as CloseIcon, Refresh as RefreshIcon } from "@mui/icons-material"
import StreamPlayer from "../components/StreamPlayer"
import { useIsMobile } from "../hooks/useMediaQuery"

const StreamViewer: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const streamUrl = searchParams.get("url")
  const cameraId = searchParams.get("camera")
  const cameraName = searchParams.get("name") || "Câmera"
  const isMobile = useIsMobile()

  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false)
  const [snackbarMessage, setSnackbarMessage] = useState<string>("")
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "info" | "warning" | "error">("info")
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0)

  // Configurar o título da página
  useEffect(() => {
    document.title = `Stream: ${cameraName}`

    // Restaurar o título original quando o componente for desmontado
    return () => {
      document.title = t("stream.systemTitle")
    }
  }, [cameraName])

  // Função para mostrar snackbar
  const showSnackbar = (message: string, severity: "success" | "info" | "warning" | "error" = "info") => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }

  // Função para entrar em tela cheia
  const enterFullscreen = () => {
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen()
    }
  }

  // Função para fechar a janela
  const closeWindow = () => {
    window.close()
  }

  // Função para atualizar o stream
  const refreshStream = () => {
    setRefreshTrigger((prev) => prev + 1)
    showSnackbar("Atualizando stream...", "info")
  }

  // Lidar com erros de reprodução
  const handleStreamError = (error: any) => {
    console.error("Erro no stream:", error)
    showSnackbar(t("stream.loadFailedCheckOnline"), "error")
  }

  // Verificar se temos uma URL para o stream
  if (!streamUrl) {
    return (
      <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <AppBar position="static">
          <Toolbar variant="dense">
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontSize: isMobile ? "1rem" : "1.25rem" }}>
              Erro
            </Typography>
            <IconButton color="inherit" onClick={closeWindow}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "background.default",
            p: 3,
          }}
        >
          <Typography color="error" variant="h5" gutterBottom sx={{ fontSize: isMobile ? "1.2rem" : "1.5rem" }}>
            URL do stream não fornecida
          </Typography>
          <Typography color="text.secondary" variant="body1" sx={{ mb: 3, textAlign: "center" }}>
            Não foi possível iniciar a visualização sem uma URL válida.
          </Typography>
          <Button variant="contained" color="primary" onClick={closeWindow}>
            Fechar janela
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#000",
      }}
    >
      <AppBar position="static" color="default">
        <Toolbar variant={isMobile ? "regular" : "dense"} sx={{ flexWrap: "wrap" }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: isMobile ? "0.9rem" : "1.25rem",
            }}
          >
            {cameraName}
          </Typography>
          <Box sx={{ display: "flex" }}>
            <Tooltip title={t("stream.refresh")}>
              <IconButton color="inherit" onClick={refreshStream} size={isMobile ? "small" : "medium"}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("stream.fullscreen")}>
              <IconButton color="inherit" onClick={enterFullscreen} size={isMobile ? "small" : "medium"}>
                <FullscreenIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("common.close")}>
              <IconButton color="inherit" onClick={closeWindow} size={isMobile ? "small" : "medium"}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#000",
          position: "relative",
        }}
      >
        <StreamPlayer
          streamUrl={streamUrl}
          cameraName={cameraName}
          width="100%"
          height="100%"
          autoPlay={true}
          onError={handleStreamError}
          showControls={true}
          onRetry={refreshStream}
        />
      </Box>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default StreamViewer
